import { useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, PanelLeft, PanelLeftClose } from 'lucide-react';
import { selectPermissionsByResource } from '@features/auth/slice';
import { ordersApi } from '@features/orders/api';
import {
  useIsSuperAdmin,
  useIsMerchant,
  useIsMerchantParent,
  useIsMultiMerchant,
  useIsVendor,
  useViewAs,
} from '@hooks/auth';
import { useSidebar } from '@hooks/ui/useSidebar';
import { useMediaQuery } from '@hooks/ui/useMediaQuery';
import { useFlagChecker } from '@hooks/feature-flags/useFeatureFlag';
import { cn } from '@/lib/utils';
import { HintTooltip } from '@components/composition/HintTooltip';
import { appNavSections, type AppNavItem } from './appNav';
import {
  isNavItemVisible,
  type PermissionMap,
  type UserContext,
} from './navVisibility';
import { RESET_SCOPE_STATE } from './scopedSegments';

// Below this viewport width the full 240px rail squeezes page content too much
// (it was the root cause of the Orders table getting
// cramped). The rail force-collapses to the icon strip here regardless of the
// persisted preference — which is preserved and restored once the viewport
// widens again.
const AUTO_COLLAPSE_MAX_WIDTH_PX = 796;

export function Sidebar(): JSX.Element {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const dispatch = useDispatch();
  const { collapsed, toggleCollapsed, isSectionCollapsed, toggleSection } = useSidebar();
  // Force the icon rail on narrow viewports (overrides — but does not clobber —
  // the persisted preference). `forceCollapsed` also hides the manual toggle,
  // since there isn't room to expand. Above the breakpoint the user's choice
  // (`collapsed`) wins again.
  const forceCollapsed = useMediaQuery(`(max-width: ${AUTO_COLLAPSE_MAX_WIDTH_PX}px)`);
  const railCollapsed = collapsed || forceCollapsed;

  // Re-clicking the active Orders nav while already on the list refetches it, so
  // a newly-submitted order appears without a manual reload. A
  // same-path click leaves the route mounted and RTK Query serves its cache, so
  // we invalidate the list tag to force the subscribed `useGetOrdersQuery` to
  // refetch — the same `OrderList` tag the bulk actions invalidate. This mirrors
  // legacy `Sidebar.handleOrdersClick` → `dispatch(getOrders({}))`. Legacy gated
  // on the exact `/orders` full path (no query string); the new Order Console
  // persists its filters in the URL query, so we match on `pathname` and refresh
  // whatever the current filters are.
  const handleNavClick = (item: AppNavItem): void => {
    if (item.path === '/orders' && location.pathname === '/orders') {
      dispatch(ordersApi.util.invalidateTags([{ type: 'OrderList', id: 'LIST' }]));
    }
  };

  const permissionIndex = useSelector(selectPermissionsByResource);
  const permissions: PermissionMap = useMemo(
    () => ({
      has: (resource, action) => permissionIndex[resource]?.has(action) ?? false,
    }),
    [permissionIndex],
  );

  const superAdmin = useIsSuperAdmin();
  const merchant = useIsMerchant();
  const vendor = useIsVendor();
  const merchantParent = useIsMerchantParent();
  const multiMerchant = useIsMultiMerchant();
  const { isViewingAsMerchant, isViewingAsVendor } = useViewAs();
  const hasFlag = useFlagChecker();

  const ctx: UserContext = {
    superAdmin,
    merchant,
    vendor,
    merchantParent,
    multiMerchant,
    viewingAsMerchant: isViewingAsMerchant,
    viewingAsVendor: isViewingAsVendor,
  };

  const hasNavAccess = appNavSections.some((section) =>
    section.items.some((item) => isNavItemVisible(item, permissions, ctx, hasFlag)),
  );

  // Active when the path matches exactly or is a sub-route. The trailing-slash
  // check (`${path}/`) requires a segment boundary so a prefix never lights up
  // a longer sibling that merely starts with the same characters.
  const matchesPrefix = (path: string): boolean =>
    location.pathname === path ||
    (path !== '/home' && location.pathname.startsWith(`${path}/`));

  // An item is active for its own path or for any `activeWhenUnder` prefix —
  // the latter covers surfaces re-rooted away from the nav path (e.g. Account
  // Info owns the merchant/vendor detail pages).
  const isActive = (item: AppNavItem): boolean =>
    matchesPrefix(item.path) ||
    (item.activeWhenUnder?.some(matchesPrefix) ?? false);

  return (
    <aside
      className={cn(
        // overflow-y-auto: the nav can exceed the viewport (a super-admin sees
        // all 14 items), and the parent row is `overflow-hidden` — without a
        // scroll here the bottom section ("Users and Roles", Webhooks) is
        // clipped and only reappears when the browser is shrunk.
        // Mirrors the sibling <main> scroll in AppLayout + legacy Sidebar.scss.
        'shrink-0 overflow-y-auto border-r bg-card/40 backdrop-blur-sm supports-backdrop-filter:bg-card/30 transition-[width] duration-200',
        railCollapsed ? 'w-16' : 'w-60',
      )}
      data-testid="sidebar"
    >
      <nav className="flex flex-col gap-2 p-2">
        {/* The manual toggle is hidden below the auto-collapse breakpoint —
            there's no room to expand, so the rail stays icon-only there. */}
        {!forceCollapsed && hasNavAccess && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={t(collapsed ? 'collapse.expand' : 'collapse.collapse', {
              defaultValue: collapsed ? 'Expand sidebar' : 'Collapse sidebar',
            })}
            aria-expanded={!collapsed}
            className={cn(
              'flex items-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
              collapsed ? 'justify-center' : 'justify-end',
            )}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        )}

        {appNavSections.map((section, idx) => {
          const visibleItems = section.items.filter((item) =>
            isNavItemVisible(item, permissions, ctx, hasFlag),
          );
          if (visibleItems.length === 0) return null;
          const sectionCollapsed = section.headingKey ? isSectionCollapsed(section.headingKey) : false;
          // In the icon rail there are no section headers to toggle, so always
          // show every item; otherwise honor the per-section collapse state.
          const showItems = railCollapsed || !sectionCollapsed;
          return (
            <div key={section.headingKey ?? `section-${idx}`} className="flex flex-col gap-1">
              {section.headingKey && !railCollapsed && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.headingKey as string)}
                  aria-expanded={!sectionCollapsed}
                  className="flex items-center justify-between rounded-md px-3 pt-3 pb-1 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-foreground"
                  data-testid={`sidebar-${section.headingKey}-toggle`}
                >
                  <span>{t(`sections.${section.headingKey}`)}</span>
                  <ChevronDown
                    className={cn('h-3.5 w-3.5 transition-transform', sectionCollapsed && '-rotate-90')}
                  />
                </button>
              )}
              {showItems &&
                visibleItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  const label = t(`items.${item.labelKey}`);
                  return (
                    <HintTooltip key={item.path} content={railCollapsed ? label : undefined} side="right">
                      <Link
                        to={item.path}
                        // Main-menu navigations always return scoped sections to
                        // their merchant/vendor picker — even a re-click of the
                        // active section. `useEntityScopeReset`
                        // consumes this marker.
                        state={{ [RESET_SCOPE_STATE]: true }}
                        onClick={() => handleNavClick(item)}
                        aria-label={railCollapsed ? label : undefined}
                        className={cn(
                          'flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
                          railCollapsed ? 'justify-center px-2' : 'px-3',
                          active
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                        )}
                        data-testid={`sidebar-link-${item.testIdSuffix ?? item.path.replaceAll('/', '-').replace(/^-/, '')}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {!railCollapsed && <span>{label}</span>}
                      </Link>
                    </HintTooltip>
                  );
                })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
